<template>
  <div class="col-xs-12 col-sm-12 offset-md-1 col-md-10">
    <el-divider class="divider-green"></el-divider>

    <div class="row d-flex justify-content-between align-items-center" ref="auditLogTop">
      <div class="block">
        <h2>Audit Log</h2>
        <p v-show="auditLog.length > 0">
          Displaying {{ currentCountSoFar }} logs of {{ totalAuditLogCount }}
        </p>
      </div>
      <div class="row d-flex align-items-center">
        <div>
          <base-button @click="exportPDF()" class="btn-dark float-sm-none float-md-left">Export PDF</base-button>
        </div>
        <div style="width: 300px; padding-left:20px">
          <el-input :disabled="!this.isOrderIdAvailable" @input="searchInput()" v-model="search"
            placeholder="Type to search" />
        </div>
      </div>
    </div>
    <br />

    <div class="block">

      <!-- Loading indicator at top when loading latest -->
      <div v-if="isLoading && loadingAction === 'latest'" class="text-center pt-3 pb-3">
        <i class="el-icon-loading" style="font-size: 24px; color: #275d34;"></i>
        <span style="margin-left: 10px; color: #275d34;">{{ loadingMessage }}</span>
      </div>

      <br />
      <el-timeline v-if="auditLog && auditLog.length && this.isOrderIdAvailable">
        <el-timeline-item v-for="item in auditLog" :timestamp="item.logDate | formatDateTime" :key="item.id"
          placement="top">
          <el-card shadow="never">
            <div class="row">
              <div class="col-md-12">
                <el-table :data="[item]" role="table" aria-label="Audit Log Entry">
                  <el-table-column prop="updateBy" label="Update By" scope="col">
                  </el-table-column>
                  <el-table-column prop="action" label="Action" scope="col">
                  </el-table-column>
                  <el-table-column prop="fieldNames" label="Field Name" scope="col">
                  </el-table-column>
                  <el-table-column label="Previous Value" scope="col">
                    <template slot-scope="scope">
                      <AuditSignature v-if="isSignatureField(scope.row.fieldNames) && isSignatureValue(scope.row.oldValues)" 
                        :signature-data="scope.row.oldValues"
                        :audit-log-id="scope.row.id"
                        value-type="old" />
                      <p v-else>{{ Util.convertAmPmTo24Hour(scope.row.oldValues) }}</p>
                    </template>
                  </el-table-column>
                  <el-table-column label="Current Value">
                    <template slot-scope="scope">
                      <AuditSignature v-if="isSignatureField(scope.row.fieldNames) && isSignatureValue(scope.row.newValues)" 
                        :signature-data="scope.row.newValues"
                        :audit-log-id="scope.row.id"
                        value-type="new" />
                      <p v-else>{{ Util.convertAmPmTo24Hour(scope.row.newValues) }}</p>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </el-card>
        </el-timeline-item>

        <!-- Show More / Scroll to Top Buttons -->
        <div class="text-center pt-4">
          <base-button
            v-if="currentPageNumber > 1"
            @click="backToLatest()"
            type="secondary"
            :disabled="isLoading"
            style="margin-right: 15px;"
          ><i v-if="isLoading && loadingAction === 'latest'" class="el-icon-loading" style="margin-right: 5px;"></i><i v-else class="tim-icons icon-tap-02" style="margin-right: 5px;"></i>{{ isLoading && loadingAction === 'latest' ? 'Loading...' : 'Return to top' }}</base-button>
          <base-button
            v-if="hasMorePages"
            @click="loadMoreAuditLogs()"
            type="primary"
            :disabled="isLoading"
          ><i v-if="isLoading && loadingAction === 'more'" class="el-icon-loading" style="margin-right: 5px;"></i>{{ isLoading && loadingAction === 'more' ? 'Loading...' : 'Show More' }}</base-button>
        </div>
        
        <!-- Loading indicator at bottom when loading more or searching -->
        <div v-if="isLoading && auditLog.length > 0 && loadingAction !== 'latest'" class="text-center pt-3 pb-3">
          <i class="el-icon-loading" style="font-size: 24px; color: #275d34;"></i>
          <span style="margin-left: 10px; color: #275d34;">{{ loadingMessage }}</span>
        </div>
      </el-timeline>

      <el-card v-if="(!auditLog || !auditLog.length || !this.isOrderIdAvailable) && !isLoading && hasLoadedOnce">
        <div class="text-center pt-4" style="min-height: 100px;">
          <h2>{{ auditLogLoadingText || 'No Audit Logs Available!' }}</h2>
        </div>
      </el-card>
      
      <!-- Initial loading state when no data yet -->
      <el-card v-if="(isLoading || !hasLoadedOnce) && (!auditLog || !auditLog.length)">
        <div class="text-center pt-4" style="min-height: 100px;">
          <i class="el-icon-loading" style="font-size: 32px; color: #275d34;"></i>
          <h3 style="margin-top: 15px; color: #275d34; font-weight: 500;">Loading Audit Logs...</h3>
        </div>
      </el-card>

    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  Divider,
  Tag,
  Card,
  Timeline,
  TimelineItem,
} from "element-ui";

import { mapActions, mapGetters, mapState } from "vuex";
import Util from "@/util/util";
import axios from '@/axios';
import AuditSignature from "@/components/App/signatures/AuditSignature";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Divider.name]: Divider,
    [Tag.name]: Tag,
    [Timeline.name]: Timeline,
    [TimelineItem.name]: TimelineItem,
    [Card.name]: Card,
    AuditSignature,
  },
  data() {
    return {
      search: "",
      resultsCount: 10,
      searchDebounceTimer: null,
      loadingAction: null,  // 'more', 'latest', or 'search'
      hasLoadedOnce: false,  // Track if initial load has completed
      Util: Util,  // Expose Util to template
    };
  },
  watch: {
    async activeTab(newTab, oldTab) {
      if (newTab == "AuditLog") {
        // Only load if we don't already have data for this order
        // (avoids re-fetching when switching tabs back)
        if (this.auditLog.length === 0 || this.currentOrderId !== this.orderId) {
          await this.loadAuditLogData(1, true);
        }
      }
    },
  },
  filters: {
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    },
  },
  computed: {
    // Use mapState for the array - more direct reactivity than getters
    ...mapState('auditLog', {
      auditLogFromState: state => state.auditLog,
      totalAuditLogCount: state => state.totalAuditLogCount,
      currentCountSoFar: state => state.currentCountSoFar,
      currentPageNumber: state => state.currentPage,
      pageSize: state => state.pageSize,
      isLoading: state => state.isLoading,
      currentOrderId: state => state.currentOrderId,
      hasMorePages: state => state.hasMorePages,
      auditLogLoadingText: state => state.loadingText,
    }),
    ...mapGetters({
      orderIdFromPreOrderDetail: "preOrderDetail/preOrder",
      activeTab: "tab/activeTab",
    }),
    auditLog() {
      if (!this.auditLogFromState) return [];
      return this.auditLogFromState;
    },
    // Calculate display range start (1-indexed)
    displayRangeStart() {
      if (!this.totalAuditLogCount || this.totalAuditLogCount === 0) return 0;
      return ((this.currentPageNumber - 1) * this.resultsCount) + 1;
    },
    // Calculate display range end
    displayRangeEnd() {
      if (!this.totalAuditLogCount) return 0;
      const end = this.currentPageNumber * this.resultsCount;
      return Math.min(end, this.totalAuditLogCount);
    },
    // Calculate total pages
    totalPages() {
      if (!this.totalAuditLogCount || !this.resultsCount) return 1;
      return Math.ceil(this.totalAuditLogCount / this.resultsCount);
    },

    orderIdFromRoute() {
      return parseInt(this.$route.params.id);
    },
    orderId() {
      if (this.isOrderIdAvailable && this.orderIdFromRoute !== 0) {
        return this.orderIdFromRoute;
      } else {
        return this.orderIdFromPreOrderDetail.orderId;
      }
    },
    isOrderIdAvailable() {
      return (
        this.orderIdFromRoute != 0 ||
        (this.orderIdFromRoute != 0 &&
          this.orderIdFromPreOrderDetail &&
          this.orderIdFromPreOrderDetail.orderId != 0)
      );
    },
    // Dynamic loading message based on action
    loadingMessage() {
      switch (this.loadingAction) {
        case 'latest':
          return 'Loading latest audit logs...';
        case 'more':
          return 'Loading more audit logs...';
        case 'search':
          return 'Searching audit logs...';
        default:
          return 'Loading audit logs...';
      }
    },
  },
  methods: {
    ...mapActions({
      fetchAuditLog: "auditLog/fetchAuditLog",
      fetchAuditLogPagination: "auditLog/fetchAuditLogPagination",
      clearAuditLogData: "auditLog/clearData"
    }),
    async loadAuditLogData(pageNumber, isclear = false) {
      if (this.isOrderIdAvailable) {
        // same way to pass apiInfor to the search, there is no export flag
        let apiInfo = {
          orderId: this.orderId,
          pageNumber: pageNumber ? pageNumber : 1,
          resultsPerPage: this.resultsCount,
          searchText: this.search,
          isClear: isclear
        };
        await this.fetchAuditLogPagination(apiInfo);
        this.hasLoadedOnce = true;
      }
    },
    async loadMoreAuditLogs() {
      this.loadingAction = 'more';
      // Use stored page number + 1 for next page (more reliable than array length calculation)
      const nextPage = this.currentPageNumber + 1;
      await this.loadAuditLogData(nextPage);
      this.loadingAction = null;
    },
    backToLatest() {
      // Simply scroll to top - preserve all loaded data
      // User requested: "When returning the user to the top please leave open the number of orders currently displayed"
      if (this.$refs.auditLogTop) {
        this.$refs.auditLogTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    // Check if a field is a signature image field (not date/time/reason fields that contain "signature")
    isSignatureField(fieldNames) {
      if (!fieldNames) return false;
      const lower = fieldNames.toLowerCase();
      // Exclude non-image fields that contain "signature" (e.g., file names like "mysignature.png")
      if (lower.includes('date') || lower.includes('time') || lower.includes('reason')) return false;
      if (lower.includes('deleted file') || lower.includes('added new file') || lower.startsWith('file')) return false;
      return lower.includes('signature');
    },
    // Check if a value is actual signature image data (base64 encoded or data URI),
    // not a date string, "N/A", or other non-image text.
    isSignatureValue(value) {
      if (!value || value === 'N/A' || value === 'null') return false;
      if (value === '[SIGNATURE_PLACEHOLDER]') return true;
      // Date values from the backend are formatted as MM/dd/yyyy or MM/dd/yyyy HH:mm:ss.
      // If the value matches a date pattern, it is not signature image data.
      if (Util.isAuditDateValue(value)) return false;
      // Any non-null, non-date value in a signature field is signature image data
      return true;
    },
    searchInput() {
      // Debounce search - wait 300ms after user stops typing
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(async () => {
        this.loadingAction = 'search';
        await this.loadAuditLogData(1, true);
        this.loadingAction = null;
      }, 300);
    },

    exportPDF() {
      this.$loaderApp.addRequest();
      // Build URL - only include search parameter if it has a value
      const searchParam = this.search ? `/${encodeURIComponent(this.search)}` : '';
      axios({
        url: `/api/orderauditlogs/pdf/${this.orderId}${searchParam}`,
        method: "GET",
        responseType: "blob", // important
      }).then((response) => {
        Util.fileDownload(response.data, `Audit_Log_Summary_${this.orderId}.pdf`);
        this.$loaderApp.completeRequest();
      }).catch((error) => {
        this.$loaderApp.completeRequest();
        // Extract message from various error formats
        let message = "An error occurred while exporting the PDF.";
        
        if (!error.response) {
          // No response -- network error, blocked, or timeout
          message = error.message || "Unable to reach the server. Please check your connection.";
        } else if (error.response.status === 500) {
          message = "Server error occurred while generating the PDF.";
        } else if (error.response.status === 404) {
          message = "Audit log not found.";
        }
        
        this.$toast.error(String(message));
      });
    },
  },
};
</script>

<style>
.row .search-box {
  margin-bottom: 5em;
}

.divider-green {
  background-color: #10b45a;
}

.el-table--border {
  border: 1px solid rgba(34, 42, 66, 0.2);
}

ul.el-timeline {
  padding-inline-start: 0;
}

/* Fix button focus/active border */
.text-center.pt-4 .btn:focus,
.text-center.pt-4 .btn:active,
.text-center.pt-4 .btn:focus:active {
  box-shadow: none !important;
  outline: none !important;
}

</style>
